import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Paper,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import FileOpen from "@mui/icons-material/FileOpen";
import FileSave from "@mui/icons-material/Save";
import { useEffect, useState } from "react";
import { workbookAtom } from "datasheets/model/atoms/WorkbookAtoms";
import {
  listWorkbooksAtom,
  saveWorkbookAtom,
} from "datasheets/model/atoms/WorkbookActionAtoms";
import { atom, useAtom, useSetAtom } from "jotai";
import { Download, Upload } from "@mui/icons-material";
import { fileOpen, fileSave } from "browser-fs-access";
import { handleErrors } from "datasheets/model/logic/handleErrors";
import { copyWorkbook } from "datasheets/model/logic/replicate";
import { BaseMenuGroup } from "./BaseMenuGroup";

const navigate = (newRoute: string) => {
  window.open(
    `${window.location.origin}${newRoute}`,
    "_blank",
    "rel=noopener noreferrer"
  );
};

const bdfContentType = "text/javascript";
const bdfExtension = ".bdf";

const downloadWorkbookAtom = atom(null, (get, set, arg) => {
  const workbook = get(workbookAtom);
  const filename = workbook.name + bdfExtension;
  const content = JSON.stringify(workbook);
  const blob = new Blob([content], { type: bdfContentType });
  fileSave(blob, {
    fileName: filename,
    extensions: [bdfExtension],
  }).catch(handleErrors);
});

const uploadWorkbookAtom = atom(null, (get, set, arg) => {
  fileOpen({
    extensions: [bdfExtension],
  })
    .then((blob) => blob.text())
    .then((text) => {
      const savedWorkbook = JSON.parse(text);
      // replicate workbook to generate new uuids
      const newWorkbook = copyWorkbook(savedWorkbook);
      set(workbookAtom, newWorkbook);
    })
    .catch(handleErrors);
});

export function FileMenu() {
  const uploadWorkbook = useSetAtom(uploadWorkbookAtom);
  const downloadWorkbook = useSetAtom(downloadWorkbookAtom);
  const saveWorkbook = useSetAtom(saveWorkbookAtom);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const newWorkbook = () => {
    navigate("/");
  };

  return (
    <>
      <BaseMenuGroup
        title="File"
        menuItems={[
          [
            {
              action: newWorkbook,
              enabled: true,
              icon: <Add fontSize="small" />,
              title: "New",
            },
            {
              action: () => setLoadModalOpen(true),
              enabled: true,
              icon: <FileOpen fontSize="small" />,
              title: "Open",
            },
            {
              action: saveWorkbook,
              enabled: true,
              icon: <FileSave fontSize="small" />,
              title: "Save",
            },
          ],
          [
            {
              action: downloadWorkbook,
              enabled: true,
              icon: <Download fontSize="small" />,
              title: "Download to file",
            },
            {
              action: uploadWorkbook,
              enabled: true,
              icon: <Upload fontSize="small" />,
              title: "Upload from file",
            },
          ],
        ]}
      />

      <FileOpenDialog
        modalIsOpen={loadModalOpen}
        setModalIsOpen={setLoadModalOpen}
      />
    </>
  );
}
interface ModalFormProps {
  modalIsOpen: boolean;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
function FileOpenDialog({ modalIsOpen, setModalIsOpen }: ModalFormProps) {
  const [workbooks, fetchWorkbooks] = useAtom(listWorkbooksAtom);
  // refresh document list
  // whenever dialog is opened or closed
  useEffect(() => {
    // Required to avoid act warning in tests
    if (process.env.NODE_ENV !== "test") {
      fetchWorkbooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen]);
  const load = (workbook_id: string) => {
    navigate("/workbook/" + workbook_id);
  };
  const [filename, setFilename] = useState<string | null>(null);

  const loadOptions =
    workbooks !== null ? (
      workbooks.map(([id, name]) => (
        <ListItem
          key={id}
          value={id}
          onClick={() => setFilename(id)}
          selected={id === filename}
        >
          {name}
        </ListItem>
      ))
    ) : (
      <></>
    );

  function handleCloseDialog() {
    setModalIsOpen(false);
    fetchWorkbooks("CLEAR");
  }
  const handleLoad = () => {
    handleCloseDialog();
    if (!!filename) {
      load(filename);
    }
  };
  return (
    <Dialog
      open={modalIsOpen}
      onClose={handleCloseDialog}
      role="fileOpenDialog"
      fullWidth={true}
    >
      <DialogTitle>Open Workbook</DialogTitle>
      <DialogContent
        sx={{
          bgcolor: "rgb(250,250,250)",
          overflowY: "hidden",
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            "box-shadow": "inset 1px 2px 5px 0px rgba(0,0,0,0.2)",
            maxHeight: "30vh",
            height: "30vh",
            overflowY: "scroll",
          }}
        >
          <List>{loadOptions}</List>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ bgcolor: "rgb(250,250,250)" }}>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button onClick={handleLoad} variant="outlined">
          Open
        </Button>
      </DialogActions>
    </Dialog>
  );
}
